import { useState, useRef, useEffect } from "react";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { sanitizeText, sanitizeEmail, sanitizePhone, sanitizeNumeric } from "@/lib/sanitize";

interface EditableCellProps {
  value: string | number | null;
  onSave: (value: string) => void;
  type?: "text" | "number" | "email";
}

export function EditableCell({ value, onSave, type = "text" }: EditableCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value?.toString() || "");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      const el = textareaRef.current;
      el.focus();
      el.select();
      // Auto-size to content
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [isEditing]);

  const autoResize = (el: HTMLTextAreaElement) => {
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const handleSave = () => {
    let sanitized = editValue;
    if (type === 'email') sanitized = sanitizeEmail(editValue);
    else if (type === 'number') sanitized = sanitizeNumeric(editValue);
    else sanitized = sanitizeText(editValue, 1000);
    
    if (sanitized !== value?.toString()) {
      onSave(sanitized);
    }
    setEditValue(sanitized);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value?.toString() || "");
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-start gap-1 w-full">
        <textarea
          ref={textareaRef}
          value={editValue}
          onChange={(e) => {
            setEditValue(e.target.value);
            autoResize(e.target);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          rows={1}
          className="flex-1 min-w-0 resize-none overflow-hidden rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring min-h-[32px]"
        />
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 shrink-0"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleSave}
        >
          <Check className="h-4 w-4 text-green-600" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 shrink-0"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleCancel}
        >
          <X className="h-4 w-4 text-red-600" />
        </Button>
      </div>
    );
  }

  return (
    <div
      className="cursor-pointer hover:bg-accent/50 px-2 py-1 rounded min-h-[32px] flex items-center"
      onClick={() => setIsEditing(true)}
    >
      {value || "-"}
    </div>
  );
}
